import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Box } from '@theme-ui/components';
import { useCombobox } from 'downshift';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import TextField from '@app/components/input/TextField';
import { getLocalizationCountryCookie } from '@app/helpers/localization';
import useLocaleWithFallback from '@app/hooks/useLocaleWithFallback';
import useValidationMessage from '@app/hooks/useValidationMessage';
import theme from '@app/styles/theme';
import { rem } from '@app/styles/theme/layout';
import { GOOGLE_PLACES_API_KEY } from '@config/common.config';

import type { AddressFormNamespaces } from '../Address.constants';
import { AddressFormFields } from '../Address.constants';
import messages from '../Address.messages';
import type { AddressFormState } from '../Address.types';
import { getAddressComponents } from './AddressFields.helpers';

export interface AddressAutoSuggestProps {
  namespace: AddressFormNamespaces;
}

const AddressAutoSuggest = ({ namespace }: AddressAutoSuggestProps) => {
  const [streetAutocomplete, setStreetAutocomplete] = useState('address-line1');

  const {
    formState: { errors },
    setValue,
  } = useFormContext<AddressFormState>();

  const { msg } = useValidationMessage(messages);

  const { getLanguagesWithFallback } = useLocaleWithFallback();

  const {
    placesService: places,
    placePredictions: suggestions,
    getPlacePredictions: getSuggestions,
    isPlacePredictionsLoading: isLoading,
  } = usePlacesService({
    language: getLanguagesWithFallback()[0],
    apiKey: GOOGLE_PLACES_API_KEY,
    debounce: 300,
    options: {
      input: '',
      types: ['address'],
      componentRestrictions: { country: getLocalizationCountryCookie() },
    },
  });

  const {
    isOpen,
    selectedItem,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    setInputValue,
    openMenu,
  } = useCombobox({
    items: suggestions,
    onSelectedItemChange: ({ selectedItem: selection }) => {
      if (!selection?.place_id) return;

      places?.getDetails({ placeId: selection.place_id }, (details) => {
        const address = getAddressComponents(details);

        setValue(
          `${namespace}.${AddressFormFields.StreetLine1}`,
          selection.structured_formatting.main_text,
        );
        setValue(
          `${namespace}.${AddressFormFields.Postcode}`,
          address.postcode,
        );
        setValue(`${namespace}.${AddressFormFields.City}`, address.city);
      });
    },
    onIsOpenChange: ({ isOpen }) => {
      setStreetAutocomplete(isOpen ? 'nope' : 'true');
    },
    itemToString: (prediction) =>
      prediction ? prediction.structured_formatting.main_text : '',
  });

  return (
    <Controller
      name={`${namespace}.${AddressFormFields.StreetLine1}`}
      defaultValue=""
      render={({ field: { ref: _ref, ...props } }) => (
        <div style={{ marginBottom: rem(12) }} {...getComboboxProps()}>
          <TextField
            ref={_ref}
            {...getInputProps({ refKey: 'ref' })}
            {...props}
            data-testid={`${namespace}-addressLine1`}
            id={`${namespace}.${AddressFormFields.StreetLine1}`}
            labelProps={getLabelProps()}
            aria-label={<FormattedMessage {...messages.addressLineOne} />}
            autoComplete={streetAutocomplete}
            maxLength={35}
            isValid={!errors[namespace]?.street?.[0]}
            formLabel={<FormattedMessage {...messages.addressLineOne} />}
            loading={isLoading}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              props.onChange(e.target.value);
              setInputValue(e.target.value);
              getSuggestions({ input: e.target.value });
              openMenu();
            }}
          />

          <ComboBox
            isOpen={isOpen && suggestions.length > 0}
            {...getMenuProps()}
          >
            {isOpen &&
              suggestions.map((prediction, index) => (
                <ComboBoxItem
                  key={prediction.place_id}
                  {...getItemProps({
                    key: prediction.place_id,
                    index,
                    item: prediction,
                    style: {
                      backgroundColor:
                        highlightedIndex === index
                          ? theme.colors.blueishGray
                          : theme.colors.white,
                      fontWeight: selectedItem === prediction ? '600' : '400',
                    },
                  })}
                >
                  <Box
                    mb={1}
                    css={css`
                      font-size: ${rem(14)};
                      font-weight: 500;
                    `}
                  >
                    {prediction.structured_formatting.main_text}
                  </Box>
                  <Box>{prediction.structured_formatting.secondary_text}</Box>
                </ComboBoxItem>
              ))}
          </ComboBox>
        </div>
      )}
      rules={{
        required: {
          value: true,
          message: msg({
            type: 'required',
            key: 'addressLineOne',
          }),
        },
      }}
    />
  );
};

const ComboBox = styled.ul<{ isOpen?: boolean }>`
  display: flex;
  flex-direction: column;
  background: white;
  position: absolute;
  z-index: 1;
  margin: 0;
  width: 100%;
  cursor: default;
  padding: 0;
  list-style: none;
  box-shadow: 0 1px 0 ${theme.colors.shadowGray};
  border-bottom-left-radius: ${rem(5)};
  border-bottom-right-radius: ${rem(5)};

  ${(props) => !props.isOpen && 'display: none'}
`;

const ComboBoxItem = styled.li`
  padding: ${rem(10)} ${rem(15)};
  font-size: ${rem(13)};
`;

export default AddressAutoSuggest;
